export interface Call {
  // Existing fields
  callId: string;
  incidentNumber: string;
  callType: string;
  callDate: string;
  processingTime: string;
  status: string;
  audioFileName: string;
  audioFileUrl?: string;
  audioBlobSasUrl?: string;
  fileDuration?: string;
  fileDurationFormatted?: string;
  overallScore?: number;
  qualityScore?: number;
  emergencyFlags?: string[];
  reviewStatus?: string; // "Auto-Audited" or "For Review"
  hasManualScoreOverrides?: boolean;
  /** The dashboard's CAD column. Absent while the agency has CAD records off. */
  cadStatus?: CadListStatus;
  manualScoreOverrideDate?: string;
  manualScoreOverrideBy?: string;
  createdDate: string;

  // Additional fields for full flexibility
  caseType?: string;
  callerName?: string;
  callerNumber?: string;
  location?: string;
  summary?: string;
  reviewRequested?: boolean;
  reviewNotes?: string;
  lastReviewDate?: string;
  lastReviewedBy?: string;
  createdBy?: string;
  modifiedDate?: string;
  modifiedBy?: string;

  /** Cosine similarity score (0-1) — populated only for semantic/similar-call results */
  matchScore?: number;
}

export type CadListStatus = 'Attached' | 'Reading' | 'Unreadable' | 'Missing';

export interface CallSearchRequest {
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  callType?: string;
  status?: string;
  reviewStatus?: string;
  operator?: string;
  /** Calls with a CAD report ("Attached") or without ("Missing"). Classic search only. */
  cadReport?: 'Attached' | 'Missing';
  minQualityScore?: number;
  maxQualityScore?: number;
  emergencyFlags?: string[];
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDescending: boolean;
}

export interface FilterValues {
  callTypes: string[];
  operators: string[];
  reviewStatuses: string[];
  caseTypes: string[];
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface CallSearchResponse extends PagedResult<Call> {
  lastModified: string;
  changesSince?: {
    added: Call[];
    updated: Call[];
    deleted: string[];
  };
}

export interface KPIData {
  totalFiles: number;
  averageScore: number;
  completionPercentage: number;
  autoAuditedPercentage: number;
}

export interface CallDetails {
  callId: string;
  incidentNumber: string;
  callType: string;
  callDate: string;
  processingTime: string;
  status: string;
  audioFileName: string;
  audioFileUrl: string;
  audioBlobSasUrl?: string;
  fileDuration?: string;
  fileDurationFormatted?: string;
  transcriptionText: string;
  summary: string;
  overallScore?: number;
  // (Legacy fields removed: caseEntryScore, cadSkillsScore, customerServiceScore, qaeOverviewScore)
  detailedScoresJson?: string; // Enhanced detailed scores JSON (only format we use)
  createdDate: string;
  modifiedDate: string;
  callerName: string;
  callerNumber: string;
  location: string;
  caseType: string;
  reviewRequested: boolean;
  reviewNotes: string;
  lastReviewDate?: string;
  lastReviewedBy: string;
  reviewStatus?: string; // "Auto-Audited" or "For Review"
  comprehensiveFeedback?: string; // Detailed AI analysis feedback per category
  aiRetryCount?: number;
  /**
   * True when the last analysis run failed. On a 'completed' call this means the run fell
   * back to the previous analysis — the scores shown are the older ones, not a fresh result.
   */
  analysisFailed?: boolean;
  /** The call taker's alias. An access control, not a label: it decides which Agent can open the call. */
  operatorId?: string | null;
  operator?: string | null;
  /** Identity and stage status of the call's CAD record. Null while the agency has CAD records off or none is attached. */
  cadRecord?: CadRecordSummary | null;
}

/** Mirrors A3.Core.Models.CadRecordSummaryDto. */
export interface CadRecordSummary {
  attachmentId: string;
  extractionStatus: CadExtractionStatus;
  extractedAt?: string | null;
  verificationStatus: CadVerificationStatus;
  verifiedAt?: string | null;
  appliedAt?: string | null;
  rescoreStatus: CadRescoreStatus;
  cadScoredAt?: string | null;
}

/** Mirrors A3.Core.Constants.CadRescoreStatus. */
export type CadRescoreStatus = 'NotRun' | 'Pending' | 'Rescoring' | 'Rescored' | 'RescoreFailed';

export const isCadRescoreInProgress = (status: CadRescoreStatus): boolean =>
  status === 'Pending' || status === 'Rescoring';

/** Mirrors A3.Core.Constants.CadVerificationStatus. */
export type CadVerificationStatus =
  | 'NotRun'
  | 'Pending'
  | 'Verifying'
  | 'Verified'
  | 'VerificationFailed';

export const isCadVerificationInProgress = (status: CadVerificationStatus): boolean =>
  status === 'Pending' || status === 'Verifying';

/** Mirrors A3.Core.Constants.CadVerificationPolicy results, plus the parser's Unclear. */
export type CadVerificationResultName = 'Match' | 'Mismatch' | 'Missing' | 'Not on call' | 'Unclear';

/** Mirrors A3.Core.Models.CadVerificationItem. Quotes the caller and the record: never log it. */
export interface CadVerificationItem {
  item: string;
  callerSaid?: string | null;
  cadContains?: string | null;
  result: CadVerificationResultName;
  notes?: string | null;
}

/** Mirrors A3.Core.Models.CadVerificationTiming: times as printed, seconds as the model computed them. */
export interface CadVerificationTiming {
  callReceived?: string | null;
  firstDataEntry?: string | null;
  natureEntered?: string | null;
  callTakeFinished?: string | null;
  firstDispatch?: string | null;
  firstArrival?: string | null;
  secondsReceivedToNatureEntered?: number | null;
  secondsReceivedToCallTakeFinished?: number | null;
  recordingLengthSeconds?: number | null;
  implication?: string | null;
}

export interface CadVerificationResult {
  items: CadVerificationItem[];
  timing?: CadVerificationTiming | null;
  summary?: string | null;
}

/**
 * Mirrors A3.Core.Models.CadVerificationDto. `result` and `text` are present only when the status
 * is Verified, and a response carrying them was a durable VIEW_CAD_RECORD read.
 */
export interface CadVerification {
  attachmentId: string;
  status: CadVerificationStatus;
  /** Fixed diagnostic wording from the server; never a value from the call or the document. */
  error?: string | null;
  model?: string | null;
  verifiedAt?: string | null;
  result?: CadVerificationResult | null;
  text?: string | null;
}

/** Call record fields an apply can write, by CallEntity property name (A3.Logic CadApplyPlanner). */
export type CadApplyField =
  | 'IncidentNumber'
  | 'CallType'
  | 'CaseType'
  | 'IncidentDate'
  | 'Location'
  | 'CallerName'
  | 'CallerNumber'
  | 'OperatorId'
  | 'Operator';

/** Mirrors A3.Core.Models.CadApplyResult. */
export interface CadApplyResult {
  applied: { field: CadApplyField; previousValue?: string | null; newValue: string }[];
  skipped: { field: CadApplyField; reason: string }[];
  appliedAt?: string | null;
}

/** Mirrors A3.Core.Constants.CadExtractionStatus. */
export type CadExtractionStatus = 'Pending' | 'Extracting' | 'Extracted' | 'ExtractionFailed';

/**
 * Mirrors A3.Core.Models.CallAttachmentDto. Metadata and stage status only: the extracted fields
 * hold caller details and come from their own audited endpoint.
 */
/** A CAD report waiting for a call. Only the document's own identifiers; nothing read from it. */
export interface CadInboxItem {
  inboxItemId: string;
  originalFileName: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadedAt: string;
  eventId?: string | null;
  /** ManyCalls, CallHasRecord or NoEventId when the sweep is leaving it for a person; null while it is still being matched. */
  holdReason?: 'ManyCalls' | 'CallHasRecord' | 'NoEventId' | 'OutOfScope' | null;
  /** True when the file name or event ID names this call's incident number. */
  matchesCall: boolean;
}

export interface CadInboxSummary {
  waiting: number;
  /** Whether the Upload page should accept PDFs alongside audio. */
  bulkUploadEnabled: boolean;
}

export interface CallAttachment {
  attachmentId: string;
  callId: string;
  kind: string;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  uploadedBy: string;
  uploadedAt: string;
  extractionStatus: CadExtractionStatus;
  /** Fixed diagnostic wording from the server; never a value from the document. */
  extractionError?: string | null;
  extractionModel?: string | null;
  extractedAt?: string | null;
  verificationStatus: CadVerificationStatus;
  verificationError?: string | null;
  verificationModel?: string | null;
  verifiedAt?: string | null;
  rescoreStatus: CadRescoreStatus;
  /** Fixed diagnostic wording from the server; never a value from the call, the document or the scores. */
  rescoreError?: string | null;
  rescoreModel?: string | null;
  /** Who asked for the last rescore: a display name, or `system` after a regeneration. */
  rescoreRequestedBy?: string | null;
  cadScoredAt?: string | null;
  appliedAt?: string | null;
  appliedBy?: string | null;
  /** Call record fields the last apply wrote, by name; empty until one. Never values. */
  appliedFields?: CadApplyField[];
}

/** Mirrors A3.Core.Constants.CadRecordPolicy.SystemAppliedBy: the row's `appliedBy` when the sweep applied it (leg 4c). */
const SYSTEM_APPLIED_BY = 'system';

/** Whether the row's last apply was the automatic one rather than a supervisor's. */
export const isAppliedAutomatically = (row: Pick<CallAttachment, 'appliedBy'>): boolean =>
  row.appliedBy === SYSTEM_APPLIED_BY;

/** Whether the row's last rescore was produced by the pipeline (a regeneration, the automatic step after a read, or the analysis call) rather than asked for by a person. */
export const isRescoredBySystem = (row: Pick<CallAttachment, 'rescoreRequestedBy'>): boolean =>
  row.rescoreRequestedBy === SYSTEM_APPLIED_BY;

/** Mirrors A3.Core.Models.CadRecord: values exactly as printed in the report, null when absent. */
export interface CadRecord {
  eventId?: string | null;
  callSource?: string | null;
  receivedAt?: string | null;
  routedAt?: string | null;
  callTakeFinishedAt?: string | null;
  firstDispatchAt?: string | null;
  firstEnRouteAt?: string | null;
  firstArriveAt?: string | null;
  lastClearAt?: string | null;
  location?: string | null;
  crossStreets?: string | null;
  businessOrPlace?: string | null;
  nature?: string | null;
  priority?: string | null;
  alarmLevel?: string | null;
  callerName?: string | null;
  callerPhone?: string | null;
  callerAddress?: string | null;
  callTaker?: string | null;
  dispatcher?: string | null;
  primeUnit?: string | null;
  unitsDispatched: string[];
  disposition?: string | null;
  remarks?: string | null;
  documentText?: string | null;
}

export const isCadExtractionInProgress = (status: CadExtractionStatus): boolean =>
  status === 'Pending' || status === 'Extracting';

export interface ReviewComment {
  commentId: string;
  callId: string;
  comment: string;
  userId: string;
  userName: string;
  createdDate: string;
  /** Set when this note corrects an earlier one. Coaching comments are append-only — the
   *  original is never overwritten and is still present in the list. */
  amendsCommentId?: string | null;
  /** Set when the note has been withdrawn. The text is retained deliberately. */
  retractedDate?: string | null;
  retractedBy?: string | null;
  retractionReason?: string | null;
}

export interface QACriteria {
  criteriaId: string;
  category: string;
  description: string;
  criteria: string; // Short display name for call details page
  weight: number;
  isActive: boolean;
}export
 interface UpdateCallScoresRequest {
  callId: string;
  // (Legacy fields removed: caseEntryScore, cadSkillsScore, customerServiceScore, qaeOverviewScore)
  reviewedBy: string;
  reviewNotes?: string;
}

export interface MassUpdateRequest {
  callIds: string[];
  markAsAudited: boolean;
  overrideScore?: number;
  comment?: string;
}

export interface MassUpdateResponse {
  updatedCount: number;
  commentsAdded: number;
  errors: string[];
}

export interface MassDeleteRequest {
  callIds: string[];
  reason?: string;
}

export interface MassDeleteResponse {
  deletedCount: number;
  blobsDeleted: number;
  errors: string[];
}

export interface SimilarCallsResponse {
  items: Call[];
  sourceIncidentNumber: string;
}

export interface SemanticSearchRequest {
  query: string;
  page: number;
  pageSize: number;
  startDate?: string;
  endDate?: string;
  status?: string;
  reviewStatus?: string;
  callType?: string;
  operator?: string;
  minQualityScore?: number;
  maxQualityScore?: number;
}

export interface SemanticSearchResponse {
  items: Call[];
  totalCount: number;
  page: number;
  pageSize: number;
  /** "semantic" when vector search ran, "keyword" when fallback was used */
  searchedBy: 'semantic' | 'keyword';
}

/**
 * AI-analysis status predicates. These mirror A3.Core.Constants.CallStatus on the backend —
 * keep them here, next to the `status` field they interpret, so the page that polls and the
 * components that render the state cannot drift apart when a status is added.
 *
 * (Deliberately not in utils/statusUtils.ts: that file's CallStatus union predates the current
 * backend vocabulary and does not include the ai_* states.)
 */
const IN_PROGRESS_STATUSES = new Set(['processing', 'claimed']);
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'ai_failed', 'ai_retry_pending']);

/** Analysis is queued or actively running in the background service. */
export const isAnalysisInProgress = (status: string): boolean =>
  IN_PROGRESS_STATUSES.has(status?.toLowerCase());

/** Analysis has settled — succeeded, or failed in a way that ends this run. */
export const isAnalysisTerminal = (status: string): boolean =>
  TERMINAL_STATUSES.has(status?.toLowerCase());

/** Terminal, but not a success — the previous scores are still what is on screen. */
export const isAnalysisFailure = (status: string): boolean =>
  isAnalysisTerminal(status) && status?.toLowerCase() !== 'completed';